<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap login-page">
        <q-form autocorrect="off">
            <div class="login-form column q-col-gutter-y-md justify-center">
                <h1 class="capitalize-text">{{ $t('actions.login') }}</h1>
                <div class="tagline">
                    {{ $t('login.to_access_your_profile_and_courses') }}
                </div>
                <div class="col">
                    <q-input
                        v-model.trim="user.email"
                        name="email"
                        :label="$t('general.email')"
                        type="text"
                        bg-color="black"
                        filled
                        autofocus
                        :error-message="emailErrors"
                        :error="emailErrors != ''"
                        @input="$v.user.email.$touch()"
                        @blur="$v.user.email.$touch()"
                        @keyup.enter="authenticate"
                    >
                        <template v-if="user.email" v-slot:append>
                            <q-icon
                                class="cursor-pointer"
                                name="cancel"
                                @click.stop.prevent="user.email = ''"
                            />
                        </template>
                    </q-input>
                </div>
                <div class="col">
                    <q-input
                        v-model="user.password"
                        name="password"
                        :label="$t('general.password')"
                        :type="isPassword ? 'password' : 'text'"
                        bg-color="black"
                        filled
                        :error-message="passwordErrors"
                        :error="passwordErrors != ''"
                        @input="$v.user.password.$touch()"
                        @blur="$v.user.password.$touch()"
                        @keyup.enter="authenticate"
                    >
                        <template v-slot:append>
                            <q-icon
                                class="cursor-pointer"
                                :name="
                                    isPassword ? 'visibility_off' : 'visibility'
                                "
                                @click="isPassword = !isPassword"
                            />

                            <q-icon
                                v-if="user.password"
                                class="cursor-pointer"
                                name="cancel"
                                @click.stop.prevent="user.password = ''"
                            />
                        </template>
                    </q-input>
                </div>
                <div class="col">
                    <q-btn
                        class="login-btn"
                        :label="$t('actions.login')"
                        text-color="white"
                        flat
                        :loading="isAuthenticating"
                        @click="authenticate"
                    />
                </div>
                <div class="col form-footer">
                    <span>
                        {{ $t('login.forgot_my') }}
                        <router-link
                            to="/recover-password"
                            class="uppercase-text"
                        >
                            {{ $t('general.password') }}
                        </router-link>
                    </span>
                    <br />
                    <span>
                        {{ $t('login.do_not_have_an_account_yet') }}
                        <router-link to="/sign-up" class="uppercase-text">
                            {{ $t('actions.signup') }}
                        </router-link>
                    </span>
                </div>
            </div>
        </q-form>
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import { required } from 'vuelidate/lib/validators'
import authenticationService from '@/services/authentication'
import libraryService from '@/services/modules/libraries'
import userConfigurationsService from '@/services/modules/userConfigurations'
import store from '@/store'
import currencies from '@/local-shared/mixins/currencies'
import wishlistService from '@/services/modules/wishlist'
import { mapActions } from 'vuex'

const loadingKeys = {
    fetchingPage: 'fetching-page',
    authenticating: 'authenticating',
    fetchingUserConfigurations: 'fetching-user-configurations',
    resultCurrency: {},
    resultLocale: {},
}

const isValidEmail = (value) => {
    // eslint-disable-next-line no-control-regex
    const emailRegExp = /(?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[A-Za-z0-9-]*[A-Za-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
    return !!emailRegExp.test(value)
}

export default {
    name: 'Login',

    components: {
        PageLoading,
    },

    mixins: [currencies],

    validations: {
        user: {
            email: {
                required,
                isValidEmail,
            },
            password: {
                required,
            },
        },
    },

    data() {
        return {
            isPassword: true,

            user: {
                email: '',
                password: '',
            },

            userLibary: [],

            configuration: [],

            currentConfiguration: {},
        }
    },

    computed: {
        isLoading() {
            return this.isAuthenticating
        },

        isAuthenticating() {
            return (
                this.loadings.filter(
                    (loading) => loading.key === loadingKeys.authenticating
                ).length > 0
            )
        },

        userLogged() {
            return store.getters['session/user']
        },

        currencyList() {
            return store.getters['currency/currencyList']
        },

        emailErrors() {
            let errors = ''

            if (!this.$v.user.email.$dirty) {
                return errors
            }

            if (!this.$v.user.email.required)
                errors = errors.concat(this.$t('validations.email_is_required'))

            if (!this.$v.user.email.isValidEmail)
                errors = errors.concat(
                    this.$t('validations.email_has_invalid_format')
                )

            return errors
        },

        passwordErrors() {
            let errors = ''

            if (!this.$v.user.password.$dirty) {
                return errors
            }

            if (!this.$v.user.password.required)
                errors = errors.concat(
                    this.$t('validations.password_is_required')
                )

            return errors
        },
    },

    created() {
        if (this.userLogged) {
            this.$router.push('/')
        }
    },

    mounted() {},

    methods: {
        ...mapActions('integrations', ['setErpIntegration']),

        async fetchWishlistCount() {
            let parameters = {
                filter: {},
            }

            let response = await wishlistService.count(parameters)

            if (response.status === 200) {
                //update wishlist store counter
                store.dispatch(
                    'wishlist/updateCount',
                    response.data.results[0].count
                )
            }
        },

        async authenticate() {
            this.$v.$touch()

            if (this.$v.$invalid || this.isAuthenticating) {
                return true
            }

            this.loadingAdd(loadingKeys.authenticating)

            let parameters = {
                data: [
                    {
                        email: this.user.email,
                        password: this.user.password,
                    },
                ],
            }

            let response = await authenticationService.login(parameters)

            if (response.status === 'success') {
                this.setErpIntegration()
                await this.checkLibrary()
                store.dispatch('cart/mergeCart', this.userLogged)
                this.$route.query.redirect
                    ? this.$router.push(this.$route.query.redirect)
                    : this.$router.push('/')
                this.$q.notify({
                    type: 'positive',
                    message: this.$t('notify.login_welcome', {
                        message: response.results[0].first_name,
                    }),
                })
                this.fetchUserConfigurations()
                this.fetchWishlistCount()
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('errors.authentication_error'),
                })
            }

            this.loadingRemove(loadingKeys.authenticating)
        },

        async fetchUserConfigurations() {
            this.loadingAdd(loadingKeys.fetchingUserConfigurations)

            let parameters = {
                token: this.userLogged.token,
                filter: {
                    id: this.userLogged.id,
                },
            }

            let response = await userConfigurationsService.retrieve(parameters)

            if (response.status === 200) {
                this.configuration = response.data
                this.currentConfiguration = JSON.parse(
                    this.configuration.results[0].configuration
                )

                for (let i = 0; i < this.currencyList.length; i++) {
                    if (
                        this.currencyList[i].id ===
                        parseInt(this.currentConfiguration.currency)
                    ) {
                        this.resultCurrency = this.currencyList[i]
                    }
                }
                this.initCurrency(this.resultCurrency)
            }

            this.loadingRemove(loadingKeys.fetchingUserConfigurations)
        },

        async checkLibrary() {
            let parameters = {
                filter: {
                    user: this.userLogged.id,
                },
            }

            let response = await libraryService.retrieve(parameters)

            if (response.data.status === 'success') {
                store.commit('cart/updateLibary', response.data.results)
                store.commit('cart/updateCartByLibrary', response.data.results)
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('errors.authentication_error'),
                })
            }
        },
    },
}
</script>
